import React from 'react'
import { FeatureType, Plan } from '../../models/plans.model'
import { Grid, Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import * as selector from '../../selectors/selectors'
import { appNote, buttonLabels } from '../shared/constants'
import GridOptionsComponent from '../shared/grid-options.component'
import { DuplicateIcon, EditIcon, HideIcon, TrashIcon, UnhideIcon } from '../../icon'
import PlanFeatureComponent from '../plans/plan-feature.component'
import ButtonComponent from '../shared/button/button.component'
import CounterComponent from '../shared/counter.component'

interface Props {
    Plan: Plan
    Index: number
    DisplayYearlyPrice?: boolean
    ShowAddOnOptions?: boolean
    HideBuyNow?: boolean
    OnClick?: () => void
    OpenDisablePlanDialog?: (id: string) => void
    SelectionCount?: number;
    MinSelection?: number;
    MaxSelection?: number;
    OnSelectionChange?: (count) => void;
    PlanSelected: boolean;
    EnableCounter: boolean;
    Duplicate?: (plan: Plan) => void;
    OpenEditPlan?: (Plan: Plan) => void;
    OpenDeletePlanDialog?: (id: string) => void;
}

const NewPlanCard = ({ Plan, Index, DisplayYearlyPrice, ShowAddOnOptions, OnClick, OpenDisablePlanDialog, SelectionCount, MinSelection, MaxSelection, OnSelectionChange, PlanSelected, EnableCounter, OpenEditPlan, Duplicate, OpenDeletePlanDialog, HideBuyNow = true }: Props) => {
    const IsMobileDevice = useSelector(selector.isMobileDevice)
    return <Grid xl={3} lg={3} md={6} sm={8} item className='my-2 mx-4'>
        <div className={`${Plan.IsDeleted ? 'plan-card-inactive box-shadow-display-background-shadow' : `plan-card box-shadow-display-background-shadow ${(Index % 2 === 1) ? "bg-color-primary-light" : "bg-color-secondary"} `} p-relative h-75`}>
            {ShowAddOnOptions && <div className='p-absolute r-1 t-3'>
                <GridOptionsComponent
                    data={
                        [
                            {
                                btnName: "Edit",
                                btnColor: "primary",
                                onClickbtn: () => { if (OpenEditPlan) { OpenEditPlan(Plan) } },
                                btnIcon: EditIcon
                            },
                            {
                                btnName: Plan.IsDeleted ? "Unhide" : "Hide",
                                btnColor: Plan.IsDeleted ? "secondary" : "primary",
                                onClickbtn: () => { if (OpenDisablePlanDialog) { OpenDisablePlanDialog(Plan.Id) } },
                                btnIcon: Plan.IsDeleted ? UnhideIcon : HideIcon
                            },
                            {
                                btnName: "Duplicate",
                                btnColor: "primary",
                                onClickbtn: () => Duplicate && Duplicate(Plan),
                                btnIcon: DuplicateIcon
                            },
                            {
                                btnName: "Delete",
                                btnColor: "primary",
                                onClickbtn: () => { if (OpenDeletePlanDialog) { OpenDeletePlanDialog(Plan.Id) } },
                                btnIcon: TrashIcon
                            }
                        ]}
                    IsTiers={(Index % 2 === 1)}
                    DisabledIcon={Plan.IsDeleted}
                /></div>
            }
            {
                DisplayYearlyPrice ?
                    <span className='my-auto'>
                        <span className={`${IsMobileDevice ? 'h1' : 'h2-bold'} f-w-bold mr-1 ${Plan.IsDeleted ? 'color-primary-accent' : (Index % 2 === 1) ? 'color-secondary' : ''}`}>${Plan.YPrice}</span>
                        <span className={`${IsMobileDevice ? 'h1' : 'h2-bold'} f-w-bold`}>
                            <span className={`${Plan.IsDeleted ? 'color-primary-accent' : (Index % 2 === 1) ? 'color-secondary' : 'color-plans-text'} plan-price`}>{Plan.Price !== 0 ? `$${Plan.Price}` : appNote.FREE}</span>
                            <span className={`${IsMobileDevice ? 'h2' : 'h6'}  ${Plan.IsDeleted ? 'color-plans-text' : (Index % 2 === 1) ? 'color-secondary' : 'color-plans-text'}`}>/ Month</span>
                        </span>
                    </span> :
                    <span className={`${IsMobileDevice ? 'h2-semibold' : 'h2-bold'} ${Plan.IsDeleted ? 'color-primary-accent' : (Index % 2 === 1) ? 'color-secondary' : 'color-add-on-text '} f-w-bold my-auto`}>{Plan.Price !== 0 ? `$${Plan.Price}` : appNote.FREE}
                        <span className={`${IsMobileDevice ? 'h2' : 'h6'} ${Plan.IsDeleted ? 'color-plans-text' : (Index % 2 === 1) ? 'color-secondary' : 'color-plans-text'} `}>/ Month</span>
                    </span>
            }
            <Typography className={` ${IsMobileDevice ? 'h1' : 'h2-semibold'} w-break mb-1 t-align-left mt-2 ${Plan.IsDeleted ? 'color-primary-accent' : (Index % 2 === 1) ? 'color-secondary' : ''}`}>{Plan.Name}</Typography>
            <div className={`mb-2 bb-primary-divider-4 pb-2`}>
                {
                    Plan.Features.filter(x => x.FeatureType === FeatureType.MAIN).sort((x) => x.DisplayOrder).map((y, i) => <Typography key={i} className={` ${IsMobileDevice ? 'h4' : 'h5-regular'} ${Plan.IsDeleted ? 'color-primary-accent' : (Index % 2 === 1) ? 'color-secondary' : 'color-primary-accent'}`}>{y.Label}</Typography>)
                }
            </div>
            <div className='d-flex flex-direction justify-between h-47 '>
                <div className={` w-p-100 w-break ${Plan.IsDeleted ? 'bg-color-plans-inactive' : 'plan-features'} h-p-95 o-y-scroll`}>
                    <Typography className={`${IsMobileDevice ? 'h4' : 'h5-regular'} mb-1 ${Plan.IsDeleted ? 'color-primary-accent' : (Index % 2 === 1) ? 'color-secondary' : ''}`}>{appNote.ADDITIONAL_FEATURES}:</Typography>
                    {
                        Plan.Features.filter(x => x.FeatureType === FeatureType.ADDITIONAL).sort((x) => x.DisplayOrder).map((x, i) =>
                            <div key={i} className={`${IsMobileDevice ? 'mb-1' : ''}`}>
                                <PlanFeatureComponent Feature={x.Label} Index={Index} DisabledIcon={Plan.IsDeleted} />
                            </div>)
                    }
                </div>

                <div >
                    {Plan.FootNote && <div className='t-align-left'>
                        <Typography className={`${IsMobileDevice ? 'h4' : 'h5-regular'} w-break mb-1 ${Plan.IsDeleted ? 'color-primary-accent' : (Index % 2 === 1) ? 'color-secondary' : ''} `} gutterBottom>{Plan.FootNote}</Typography>
                    </div>}

                    {HideBuyNow && !SelectionCount &&
                        <ButtonComponent
                            OnClick={() => { OnSelectionChange(1) }}
                            ButtonLabel={buttonLabels.ADD_PLUS}
                            ClassName={`mx-auto mt-2 br-6   w-p-100 t-align-center justify-center ${IsMobileDevice ? 'h2' : 'h4-regular'} ${!PlanSelected && ((Index % 2 === 1) ? "add-on-secondary-btn-hover" : "add-on-primary-btn-hover")}`}
                            Variant={(Plan.IsDeleted || PlanSelected) ? "disabled-btn" : (Index % 2 === 1) ? "secondary-btn" : "primary-btn"}
                        />
                    }
                    {
                        !!SelectionCount && <div className='d-flex justify-center'>
                            <CounterComponent
                                inverted={!!((Index) % 2)}
                                count={SelectionCount || 0}
                                min={MinSelection === undefined ? 0 : MinSelection}
                                max={MaxSelection}
                                onChange={(count) => EnableCounter && OnSelectionChange && OnSelectionChange(count)}
                                IsMobileDevice={IsMobileDevice}
                            />
                        </div>
                    }
                </div>
            </div>
        </div>
    </Grid>
}

export default React.memo(NewPlanCard)